import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';

import messages from '../../../locale/jsonMessages';
import {
  saveInspectionDraft, validateInspection, contestInspection
} from '../../../actions/Inspection/inspection';
import {
  formFromInspection, validateForm, buildInspectionInput, photoIdsOf, isVersionConflict, FORM_LIMITS
} from '../../../helpers/inspectionForm';
import history from '../../../core/history';
import useInspectionData from '../useInspectionData';
import usePhotoSeries from '../usePhotoSeries';
import InspectionShell from '../InspectionShell';
import InspectionBanner from '../InspectionBanner/InspectionBanner';
import InspectionPhotoGrid from '../InspectionPhotoGrid/InspectionPhotoGrid';
import GeneralStateSelector from '../GeneralStateSelector/GeneralStateSelector';
import MileageInput from '../MileageInput/MileageInput';
import FuelLevelSelector from '../FuelLevelSelector/FuelLevelSelector';
import ChecklistSection from '../ChecklistSection/ChecklistSection';
import InspectionDetails from '../InspectionDetails/InspectionDetails';
import InspectionStatusBadge from '../InspectionStatusBadge/InspectionStatusBadge';
import s from '../Inspection.css';

const TYPE = 'checkin';

// Check-in on one page (R2). The owner fills it and validates; the renter gets a read-only recap with "Edit" (next version,
// validations reset) and "Validate". A modification is only possible before the record is locked (validated by both).
export function CheckinPage({ reservationId }) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { state, reload, setState } = useInspectionData({ reservationId, type: TYPE });
  const [form, setForm] = useState(formFromInspection(null, TYPE, []));
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [errors, setErrors] = useState({});
  const [contest, setContest] = useState('');
  const versionRef = useRef(null);
  const seeded = useRef(null);

  const inspection = state.kind === 'ready' ? state.inspection : null;
  const checklistItems = state.kind === 'ready' ? state.checklist : [];
  const role = state.kind === 'ready' ? state.result.viewerRole : null;
  const windowOpen = state.kind === 'ready' ? state.result.windowOpen : false;

  const onVersion = useCallback((version) => { versionRef.current = version; }, []);
  const series = usePhotoSeries({
    reservationId, type: TYPE, kind: 'vehicle', initial: [], onVersion,
    ensureReady: async () => ensureDraft(),
    onError: (failure) => setNotice({ tone: 'danger', text: failure.serverMessage || formatMessage(messages[failure.messageKey]) })
  });
  const seriesRef = useRef(series);
  seriesRef.current = series;
  const formRef = useRef(form);
  formRef.current = form;
  const checklistRef = useRef(checklistItems);
  checklistRef.current = checklistItems;

  // Seeds the form and the photos each time the server data is (re)loaded.
  useEffect(() => {
    if (state.kind !== 'ready') return;
    const key = `${state.inspection ? state.inspection.id : 'none'}:${state.inspection ? state.inspection.version : 0}`;
    if (seeded.current === key) return;
    seeded.current = key;
    versionRef.current = state.inspection ? state.inspection.version : null;
    setForm(formFromInspection(state.inspection, TYPE, state.checklist));
    seriesRef.current.reset(state.inspection ? state.inspection.photos : []);
  }, [state]);

  const mode = useMemo(() => {
    if (state.kind !== 'ready') return 'loading';
    if (!inspection) return role === 'host' && windowOpen ? 'edit' : 'empty';
    if (editing) return 'edit';
    if (inspection.status === 'draft') return inspection.canEdit ? 'edit' : 'readonly';
    if (inspection.status === 'to_validate' && inspection.canValidate) return 'recap';
    return 'readonly';
  }, [state, inspection, role, windowOpen, editing]);

  const settingsPhotoMax = FORM_LIMITS.photoMax;
  const photoCount = series.items.filter((item) => item.status !== 'failed').length;

  const applyResult = (result) => {
    if (result && result.inspection) {
      seeded.current = null;
      setState((current) => ({ ...current, result: { ...current.result, ...result }, inspection: result.inspection }));
      versionRef.current = result.inspection.version;
    }
  };

  const failureText = (result) => (result && result.errorMessage) || formatMessage(messages.inspErrGeneric);

  // After a refused write: reload and tell the user if somebody else changed the record meanwhile.
  const handleFailure = async (result, displayedVersion) => {
    const reloaded = await reload();
    seeded.current = null;
    if (reloaded && reloaded.kind === 'ready' && isVersionConflict(displayedVersion, reloaded.inspection)) {
      setEditing(false);
      setNotice({ tone: 'warning', text: formatMessage(messages.inspVersionConflict) });
    } else {
      setNotice({ tone: 'danger', text: failureText(result) });
    }
  };

  const persist = async ({ complete }) => {
    const input = { ...buildInspectionInput(formRef.current, TYPE, { draft: !complete }), photoIds: photoIdsOf(seriesRef.current.readyItems) };
    return dispatch(saveInspectionDraft({ reservationId, type: TYPE, input, expectedVersion: versionRef.current }));
  };

  // First photo of a new check-in: the draft must exist before the upload.
  async function ensureDraft() {
    if (versionRef.current !== null) return true;
    const result = await persist({ complete: false });
    if (result && result.status === 'success' && result.inspection) {
      versionRef.current = result.inspection.version;
      seeded.current = `${result.inspection.id}:${result.inspection.version}`;
      return true;
    }
    setNotice({ tone: 'danger', text: failureText(result) });
    return false;
  }

  const setField = (field, value) => { setForm((current) => ({ ...current, [field]: value })); setErrors((current) => ({ ...current, [field]: null })); };
  const toggleCheck = (key, present) => setForm((current) => ({ ...current, checklist: current.checklist.map((row) => (row.key === key ? { ...row, present } : row)) }));

  const checkErrors = () => {
    const list = validateForm(formRef.current, { type: TYPE, photoCount });
    const map = {};
    list.forEach((item) => { map[item.field] = item.messageKey; });
    setErrors(map);
    return list.length === 0;
  };

  const saveDraft = async () => {
    setBusy(true); setNotice(null);
    const displayed = versionRef.current;
    const result = await persist({ complete: false });
    if (result && result.status === 'success') {
      applyResult(result);
      setNotice({ tone: 'success', text: formatMessage(messages.inspDraftSaved) });
    } else await handleFailure(result, displayed);
    setBusy(false);
  };

  // Owner: saves the complete form then validates the version just saved.
  const submitCheckin = async () => {
    setNotice(null);
    if (!checkErrors()) { setNotice({ tone: 'danger', text: formatMessage(messages.inspFixErrors) }); return; }
    setBusy(true);
    const displayed = versionRef.current;
    const saved = await persist({ complete: true });
    if (!saved || saved.status !== 'success') { await handleFailure(saved, displayed); setBusy(false); return; }
    const validated = await dispatch(validateInspection({ reservationId, type: TYPE, version: saved.inspection.version }));
    if (validated && validated.status === 'success') {
      applyResult(validated);
      setEditing(false);
      setNotice({ tone: 'success', text: formatMessage(validated.inspection.status === 'validated' ? messages.inspCheckinLocked : messages.inspCheckinSubmitted) });
    } else await handleFailure(validated, saved.inspection.version);
    setBusy(false);
  };

  // Renter (or owner) editing a submitted check-in: the next version is created, validations reset (R4, R6).
  const saveModification = async () => {
    setNotice(null);
    if (!checkErrors()) { setNotice({ tone: 'danger', text: formatMessage(messages.inspFixErrors) }); return; }
    setBusy(true);
    const displayed = versionRef.current;
    const result = await persist({ complete: true });
    if (result && result.status === 'success') {
      applyResult(result);
      setEditing(false);
      setNotice({ tone: 'success', text: formatMessage(messages.inspModificationSaved) });
    } else await handleFailure(result, displayed);
    setBusy(false);
  };

  const validateRecap = async () => {
    setBusy(true); setNotice(null);
    const displayed = inspection.version;
    const result = await dispatch(validateInspection({ reservationId, type: TYPE, version: displayed }));
    if (result && result.status === 'success') {
      applyResult(result);
      setNotice({ tone: 'success', text: formatMessage(result.inspection.status === 'validated' ? messages.inspCheckinLocked : messages.inspCheckinSubmitted) });
    } else await handleFailure(result, displayed);
    setBusy(false);
  };

  const sendContest = async () => {
    if (!contest.trim()) { setNotice({ tone: 'danger', text: formatMessage(messages.inspErrStatementRequired) }); return; }
    setBusy(true); setNotice(null);
    const result = await dispatch(contestInspection({ reservationId, type: TYPE, comment: contest.trim() }));
    if (result && result.status === 'success') { applyResult(result); setContest(''); setNotice({ tone: 'success', text: formatMessage(messages.inspContestSent) }); }
    else setNotice({ tone: 'danger', text: failureText(result) });
    setBusy(false);
  };

  useEffect(() => {
    if (state.kind === 'notLoggedIn' && typeof window !== 'undefined') history.push('/login');
  }, [state.kind]);

  const isNew = !inspection || inspection.status === 'draft';
  const subtitle = formatMessage(mode === 'recap' ? messages.inspCheckinRecapSubtitle : messages.inspCheckinSubtitle);

  return (
    <InspectionShell title={formatMessage(messages.inspCheckinTitle)} subtitle={subtitle} state={state} bannerWhenClosed>
      {notice && <InspectionBanner tone={notice.tone}>{notice.text}</InspectionBanner>}

      {mode === 'empty' && role === 'guest' && <InspectionBanner tone="info" title={formatMessage(messages.inspCheckinWaitingOwner)} />}

      {inspection && mode !== 'edit' && (
        <p><InspectionStatusBadge status={inspection.status} /></p>
      )}

      {mode === 'recap' && (
        <>
          <InspectionBanner tone="info" title={formatMessage(messages.inspRecapIntroTitle)}>{formatMessage(messages.inspRecapIntro)}</InspectionBanner>
          <InspectionDetails inspection={inspection} type={TYPE} checklistItems={checklistItems} max={settingsPhotoMax} />
          <div className={s.actions}>
            <button type="button" className={cx(s.button, s.buttonSecondary)} disabled={busy || !inspection.canEdit} onClick={() => { setEditing(true); setNotice(null); }}>{formatMessage(messages.inspEdit)}</button>
            <button type="button" className={s.button} disabled={busy} onClick={validateRecap}>{formatMessage(messages.inspValidateCheckin)}</button>
          </div>
        </>
      )}

      {mode === 'readonly' && inspection && (
        <>
          {inspection.status === 'to_validate' && <InspectionBanner tone="info" title={formatMessage(messages.inspWaitingOtherParty)} />}
          {inspection.status === 'validated' && <InspectionBanner tone="success" title={formatMessage(messages.inspLockedBanner)}>{formatMessage(messages.inspLockedHint)}</InspectionBanner>}
          <InspectionDetails inspection={inspection} type={TYPE} checklistItems={checklistItems} max={settingsPhotoMax} />
          <div className={s.actions}>
            {inspection.status === 'to_validate' && inspection.canEdit && (
              <button type="button" className={cx(s.button, s.buttonSecondary)} disabled={busy} onClick={() => { setEditing(true); setNotice(null); }}>{formatMessage(messages.inspEdit)}</button>
            )}
          </div>
          {inspection.canContest && inspection.status === 'validated' && (
            <section className={s.card}>
              <h3 className={s.cardTitle}>{formatMessage(messages.inspContestTitle)}</h3>
              <p className={s.cardHint}>{formatMessage(messages.inspContestHint)}</p>
              <label className={s.fieldLabel} htmlFor="inspection-contest">{formatMessage(messages.inspCommentTitle)}</label>
              <textarea id="inspection-contest" className={s.textarea} value={contest} maxLength={FORM_LIMITS.comment} onChange={(event) => setContest(event.target.value)} />
              <p className={s.charCount}>{contest.length}/{FORM_LIMITS.comment}</p>
              <div className={s.actions}><button type="button" className={cx(s.button, s.buttonSecondary)} disabled={busy} onClick={sendContest}>{formatMessage(messages.inspContestSend)}</button></div>
            </section>
          )}
        </>
      )}

      {mode === 'edit' && (
        <form noValidate onSubmit={(event) => event.preventDefault()}>
          {inspection && inspection.status === 'to_validate' && <InspectionBanner tone="info" title={formatMessage(messages.inspEditIntroTitle)}>{formatMessage(messages.inspEditIntro)}</InspectionBanner>}
          <section className={s.card}>
            <h3 className={s.cardTitle}><span className={s.step} aria-hidden="true">1</span>{formatMessage(messages.inspPhotosVehicle)}</h3>
            <p className={s.cardHint}>{formatMessage(messages.inspPhotosVehicleHint)}</p>
            <InspectionPhotoGrid
              items={series.items} max={settingsPhotoMax} editable
              onAdd={series.add} onRemove={series.remove} onRetry={series.retry}
              title={formatMessage(messages.inspPhotosVehicle)}
            />
            {errors.photos && <p className={s.errorText} role="alert">{formatMessage(messages[errors.photos])}</p>}
          </section>

          <section className={s.card}>
            <h3 className={s.cardTitle}><span className={s.step} aria-hidden="true">2</span>{formatMessage(messages.inspGeneralStateTitle)}</h3>
            <p className={s.cardHint}>{formatMessage(messages.inspGeneralStateHint)}</p>
            <GeneralStateSelector value={form.generalState} onChange={(value) => setField('generalState', value)} invalid={Boolean(errors.generalState)} />
            {errors.generalState && <p className={s.errorText} role="alert">{formatMessage(messages[errors.generalState])}</p>}
          </section>

          <div className={s.grid2}>
            <section className={s.card}>
              <h3 className={s.cardTitle}><span className={s.step} aria-hidden="true">3</span>{formatMessage(messages.inspMileageTitle)}</h3>
              <p className={s.cardHint}>{formatMessage(messages.inspMileageHint)}</p>
              <MileageInput id="checkin-mileage" value={form.mileage} onChange={(value) => setField('mileage', value)} errorKey={errors.mileage} />
            </section>
            <section className={s.card}>
              <h3 className={s.cardTitle}><span className={s.step} aria-hidden="true">4</span>{formatMessage(messages.inspFuelTitle)}</h3>
              <p className={s.cardHint}>{formatMessage(messages.inspFuelHint)}</p>
              <FuelLevelSelector value={form.fuelLevel} onChange={(value) => setField('fuelLevel', value)} name="checkin-fuel" invalid={Boolean(errors.fuelLevel)} />
              {errors.fuelLevel && <p className={s.errorText} role="alert">{formatMessage(messages[errors.fuelLevel])}</p>}
            </section>
          </div>

          <section className={s.card}>
            <h3 className={s.cardTitle}><span className={s.step} aria-hidden="true">5</span>{formatMessage(messages.inspChecklistTitle)}</h3>
            <p className={s.cardHint}>{formatMessage(messages.inspChecklistHint)}</p>
            <ChecklistSection items={checklistItems} answers={form.checklist} onChange={toggleCheck} />
          </section>

          <section className={s.card}>
            <div className={s.switchRow}>
              <div>
                <h3 className={s.cardTitle}><span className={s.step} aria-hidden="true">6</span>{formatMessage(messages.inspKeysTitle)}</h3>
                <p className={s.cardHint}>{formatMessage(messages.inspKeysHint)}</p>
              </div>
              <input type="checkbox" role="switch" className={s.switchInput} checked={form.keysConfirmed} aria-label={formatMessage(messages.inspKeysTitle)}
                onChange={(event) => setField('keysConfirmed', event.target.checked)} />
            </div>
          </section>

          <section className={s.card}>
            <h3 className={s.cardTitle}><span className={s.step} aria-hidden="true">7</span>{formatMessage(messages.inspCommentOptionalTitle)}</h3>
            <p className={s.cardHint}>{formatMessage(messages.inspCommentHint)}</p>
            <textarea className={cx(s.textarea, errors.comment && s.inputError)} value={form.comment} maxLength={FORM_LIMITS.comment}
              placeholder={formatMessage(messages.inspCommentPlaceholder)} aria-label={formatMessage(messages.inspCommentTitle)}
              onChange={(event) => setField('comment', event.target.value)} />
            <p className={s.charCount}>{form.comment.length}/{FORM_LIMITS.comment}</p>
          </section>

          <div className={s.actions}>
            {isNew ? (
              <>
                <button type="button" className={cx(s.button, s.buttonSecondary)} disabled={busy || series.busy} onClick={saveDraft}>{formatMessage(messages.inspSaveDraft)}</button>
                <button type="button" className={s.button} disabled={busy || series.busy} onClick={submitCheckin}>{formatMessage(messages.inspValidateCheckin)}</button>
              </>
            ) : (
              <>
                <button type="button" className={cx(s.button, s.buttonSecondary)} disabled={busy} onClick={() => { setEditing(false); seeded.current = null; reload(); setNotice(null); }}>{formatMessage(messages.inspCancel)}</button>
                <button type="button" className={s.button} disabled={busy || series.busy} onClick={saveModification}>{formatMessage(messages.inspSaveModifications)}</button>
              </>
            )}
          </div>
          {isNew && <p className={s.infoStrip}>{formatMessage(messages.inspCheckinInfoOtherParty)}</p>}
        </form>
      )}
    </InspectionShell>
  );
}

CheckinPage.propTypes = { reservationId: PropTypes.number.isRequired };

export default withStyles(s)(CheckinPage);
